import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { CreateBrief, CreateBriefVariables } from '__generated__/CreateBrief';
import { GetDraftBriefs } from '__generated__/GetDraftBriefs';
import { GetDraftBriefsAndCookiedBrief } from '__generated__/GetDraftBriefsAndCookiedBrief';
import {
    BriefSchema,
    BriefState,
    SmartShootPhotographyPackage,
    SmartShootVideoPackage,
} from '__generated__/types';
import ApolloClient from 'apollo-client';
import Cookies from 'cookies-js';
import gql from 'graphql-tag';
import { routes } from 'projects/routes';
import { history } from 'projects/routes/BrowserRouter';
import { CallFnOnMount } from 'projects/util/mount';
import React from 'react';
import { ApolloConsumer, Mutation, MutationResult } from 'react-apollo';
import mutation from './CreateBrief.graphql';

export const GET_DRAFT_BRIEFS_AND_COOKIED_BRIEF = gql`
    query GetDraftBriefsAndCookiedBrief($id: String!, $schemas: [BriefSchema!]) {
        recentDraftStructuredBriefs(limit: 1, schemas: $schemas) {
            id
        }
        structuredBrief(id: $id) {
            id
            state
        }
    }
`;

export const GET_DRAFT_BRIEFS = gql`
    query GetDraftBriefs($schemas: [BriefSchema!]) {
        recentDraftStructuredBriefs(limit: 1, schemas: $schemas) {
            id
        }
    }
`;

type BriefType = 'video' | 'photo';
type MutationCallback = (briefId: string) => void;

interface Props {
    children: (
        mutation: (cb: MutationCallback) => void,
        result: MutationResult
    ) => React.ReactElement;
    type: BriefType;
}

class CreateSmartShootBrief extends React.Component<Props> {
    render() {
        const { children } = this.props;
        const { query, variables, handler, getBriefId } = briefConfigByType(this.props.type);
        return (
            <Mutation<CreateBrief, CreateBriefVariables> mutation={query} variables={variables}>
                {(mutation, result) => {
                    return children(cb => {
                        mutation()
                            .then(res => {
                                if (res && res.data) {
                                    if (cb) {
                                        cb(getBriefId(res.data));
                                    }
                                    handler(res.data);
                                }
                            })
                            .catch(e => {
                                this.setState(() => {
                                    throw e;
                                });
                            });
                    }, result);
                }}
            </Mutation>
        );
    }
}

const VIDEO_BRIEF_COOKIE_KEY = 'last_video_production_brief_id';
const PHOTO_BRIEF_COOKIE_KEY = 'last_photography_brief_id';

function briefConfigByType(type: BriefType) {
    switch (type) {
        case 'video':
            return {
                cookieKey: VIDEO_BRIEF_COOKIE_KEY,
                query: mutation,
                schema: BriefSchema.SMARTSHOOT_VIDEO,
                variables: {
                    fields: {
                        smartShootVideo: {
                            package: getPreselectedPackage<SmartShootVideoPackage>(location),
                        },
                    },
                } as CreateBriefVariables,
                handler: (data: CreateBrief) => {
                    history.replace(routes.videoBrief(data.createStructuredBrief.id));
                },
                replaceRoute: (briefId: string) => {
                    history.replace(routes.videoBrief(briefId));
                },
                getBriefId: (data: CreateBrief) => data.createStructuredBrief.id,
            };
        case 'photo':
            return {
                cookieKey: PHOTO_BRIEF_COOKIE_KEY,
                query: mutation,
                schema: BriefSchema.SMARTSHOOT_PHOTOGRAPHY,
                variables: {
                    fields: {
                        smartShootPhotography: {
                            package: getPreselectedPackage<SmartShootPhotographyPackage>(location),
                        },
                    },
                } as CreateBriefVariables,
                handler: (data: CreateBrief) => {
                    history.replace(routes.photoBrief(data.createStructuredBrief.id));
                },
                replaceRoute: (briefId: string) => {
                    history.replace(routes.photoBrief(briefId));
                },
                getBriefId: (data: CreateBrief) => data.createStructuredBrief.id,
            };
    }
}

function getPreselectedPackage<T extends SmartShootVideoPackage | SmartShootPhotographyPackage>(
    location: any
): T | null {
    const pkg = new URLSearchParams(location.search).get('package');
    switch (pkg) {
        case 'BASIC':
        case 'PREMIUM':
            return pkg as T;
        default:
            return null;
    }
}

interface ChildProps {
    type: BriefType;
}

const CreateSmartShootBriefOnMount = ({ type }: ChildProps) => (
    <CreateSmartShootBrief type={type}>
        {mutation => (
            <ApolloConsumer>
                {client => (
                    <CallFnOnMount
                        onMount={() => createOrRedirectToDraftBrief(client, mutation, type)}
                    >
                        <LoadableElement variations={['fade-in']} isLoading={true} />
                    </CallFnOnMount>
                )}
            </ApolloConsumer>
        )}
    </CreateSmartShootBrief>
);

function hasDraftStructuredBrief(data: GetDraftBriefsAndCookiedBrief | GetDraftBriefs) {
    return data.recentDraftStructuredBriefs && data.recentDraftStructuredBriefs.length > 0;
}

function isCookiedBriefDraft(data: GetDraftBriefsAndCookiedBrief) {
    return data.structuredBrief && data.structuredBrief.state == BriefState.DRAFT;
}

async function createOrRedirectToDraftBrief(
    client: ApolloClient<any>,
    mutation: (cb: MutationCallback) => void,
    type: BriefType
) {
    const { cookieKey, replaceRoute, schema } = briefConfigByType(type);

    const cookiedBriefId = Cookies.get(cookieKey);

    if (cookiedBriefId) {
        const { data } = await client.query<GetDraftBriefsAndCookiedBrief>({
            query: GET_DRAFT_BRIEFS_AND_COOKIED_BRIEF,
            variables: {
                id: cookiedBriefId,
                schemas: [schema],
            },
        });

        if (hasDraftStructuredBrief(data)) {
            replaceRoute(data.recentDraftStructuredBriefs[0].id);
            return;
        }

        if (isCookiedBriefDraft(data)) {
            replaceRoute(data.structuredBrief.id);
            return;
        }
    } else {
        const { data } = await client.query<GetDraftBriefs>({
            query: GET_DRAFT_BRIEFS,
            variables: {
                schemas: [schema],
            },
        });

        if (hasDraftStructuredBrief(data)) {
            replaceRoute(data.recentDraftStructuredBriefs[0].id);
            return;
        }
    }

    mutation(briefId => {
        Cookies.set(cookieKey, briefId);
    });
}

export default CreateSmartShootBriefOnMount;
